import { Injectable } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import {RuntimeCacheService} from './runtime-cache.service';

@Injectable({
    providedIn: 'root'
})
export class RequestCacheService {
    private readonly values = new Map<string, unknown>();
    private readonly requests = new Map<string, Promise<unknown>>();

    constructor(private readonly runTimeCache: RuntimeCacheService) {}

    getValue<T>(key: string): T | undefined {
        return this.values.get(key) as T | undefined;
    }

    setValue<T>(key: string, value: T): void {
        this.values.set(key, value);
    }

    clear(key: string): void {
        this.values.delete(key);
        this.requests.delete(key);
    }

    clearAll(): void {
        this.values.clear();
        this.requests.clear();
    }

    getCached<T>(config: {
        key: string;
        loader: () => Observable<T> | Promise<T>;
        forceReload?: boolean;
        requireAuth?: boolean;
        onCached?: (value: T) => void;
        onSuccess?: (value: T) => void;
        onError?: (err: any) => void;
    }): Promise<T> {
        const {
            key,
            loader,
            forceReload = false,
            requireAuth = true,
            onCached,
            onSuccess,
            onError
        } = config;

        if (requireAuth && !this.runTimeCache.isLogged()) {
            this.runTimeCache.resetAuthState();
            return Promise.reject(new Error('NOT_AUTHENTICATED'));
        }

        if (!forceReload && this.values.has(key)) {
            const cached = this.values.get(key) as T;
            onCached?.(cached);
            return Promise.resolve(cached);
        }

        if (!forceReload && this.requests.has(key)) {
            return this.requests.get(key) as Promise<T>;
        }

        const result = loader();
        const promise = result instanceof Promise ? result : firstValueFrom(result);

        const trackedPromise = promise
            .then((value) => {
                this.values.set(key, value);
                onSuccess?.(value);
                return value;
            })
            .catch((err) => {
                onError?.(err);

                if (err?.status === 401) {
                    this.runTimeCache.resetAuthState();
                }

                throw err;
            })
            .finally(() => {
                this.requests.delete(key);
            });

        this.requests.set(key, trackedPromise);
        return trackedPromise;
    }
}